import * as yup from "yup";
import FormConfig from "../../Config/FormConfig.js";

export const ACCOUNT_CHANGE_REQUEST_TABS = [
  { key: "shipping", label: "Shipping" },
  { key: "billing", label: "Billing" },
  { key: "account", label: "Account" },
  { key: "notes", label: "Notes" },
];

export const REQUESTER_NOTES_FIELD = "RequesterNotes";
export const MAX_REQUESTER_NOTES_LENGTH = 2000;

export const ACCOUNT_CHANGE_REQUEST_FIELDS = [
  { key: "AddressLine1", label: "Address Line 1", tab: "shipping", required: true },
  { key: "AddressLine2", label: "Address Line 2", tab: "shipping" },
  { key: "City", label: "City", tab: "shipping" },
  { key: "State", label: "State", tab: "shipping" },
  { key: "Zip", label: "ZIP", tab: "shipping" },
  { key: "BillingAddressLine1", label: "Billing Address Line 1", tab: "billing" },
  { key: "BillingAddressLine2", label: "Billing Address Line 2", tab: "billing" },
  { key: "BillingCity", label: "Billing City", tab: "billing" },
  { key: "BillingState", label: "Billing State", tab: "billing" },
  { key: "BillingZip", label: "Billing ZIP", tab: "billing" },
  { key: "APContact", label: "AP Contact", tab: "billing" },
  { key: "AccountName", label: "Account Name", tab: "account", required: true },
  { key: "Phone", label: "Phone", tab: "account" },
  { key: "Fax", label: "Fax", tab: "account" },
  {
    key: "MedicareAdminContractor",
    label: "Medicare Admin Contractor",
    tab: "account",
  },
  { key: "NPI", label: "NPI", tab: "account" },
  { key: "TIN", label: "TIN", tab: "account" },
  { key: "PTAN", label: "PTAN", tab: "account" },
  { key: "EIN", label: "EIN", tab: "account" },
];

export const ACCOUNT_CHANGE_FIELD_LABELS = Object.fromEntries(
  ACCOUNT_CHANGE_REQUEST_FIELDS.map((field) => [field.key, field.label])
);

const existingValidators = new Map(
  FormConfig.CreateAccountForm.filter((field) => field.validationFunction).map(
    (field) => [field.backendName, field.validationFunction]
  )
);

const optionalTextValidator = yup
  .string()
  .nullable()
  .max(255, "Must be 255 characters or fewer");

const accountChangeRequestValidationFields = ACCOUNT_CHANGE_REQUEST_FIELDS.reduce(
  (schema, field) => {
    const sharedValidator = existingValidators.get(field.key);
    schema[field.key] =
      sharedValidator ||
      (field.required
        ? optionalTextValidator.required(`${field.label} is required`)
        : optionalTextValidator);
    return schema;
  }, {}
);

accountChangeRequestValidationFields[REQUESTER_NOTES_FIELD] = yup
  .string()
  .nullable()
  .max(
    MAX_REQUESTER_NOTES_LENGTH,
    `Notes must be ${MAX_REQUESTER_NOTES_LENGTH.toLocaleString("en-US")} characters or fewer`
  );

export const accountChangeRequestValidationSchema = yup.object().shape(
  accountChangeRequestValidationFields
);

export const normalizeAccountChangeValue = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const normalizeRequesterNotes = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const buildAccountChangeInitialValues = (account = {}) => {
  const values = ACCOUNT_CHANGE_REQUEST_FIELDS.reduce((result, field) => {
    result[field.key] = normalizeAccountChangeValue(account[field.key]) ?? "";
    return result;
  }, {});
  values[REQUESTER_NOTES_FIELD] = "";
  return values;
};

export const diffAccountChangeValues = (original = {}, edited = {}) =>
  ACCOUNT_CHANGE_REQUEST_FIELDS.reduce((changes, field) => {
    const originalValue = normalizeAccountChangeValue(original[field.key]);
    const editedValue = normalizeAccountChangeValue(edited[field.key]);
    if (originalValue !== editedValue) changes[field.key] = editedValue;
    return changes;
  }, {});
